/**
 * Utility functions for API operations
 */

/**
 * Sanitizes payload by converting empty strings to undefined
 * This prevents backend validation errors that reject empty strings
 */
export const sanitizePayload = <T extends Record<string, unknown>>(payload: T): T => {
  const sanitized = {...payload}

  for (const key in sanitized) {
    if (Object.prototype.hasOwnProperty.call(sanitized, key)) {
      const value = sanitized[key]

      if (value === '' || (typeof value === 'string' && value.trim() === '')) {
        // Convert empty strings to undefined
        ;(sanitized as Record<string, unknown>)[key] = undefined
      } else if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        value.constructor === Object &&
        !(value instanceof Date)
      ) {
        // Recursively sanitize nested objects
        ;(sanitized as Record<string, unknown>)[key] = sanitizePayload(
          value as Record<string, unknown>,
        )
      }
    }
  }

  return sanitized
}

/**
 * API Error Handling Pattern:
 *
 * API services should just throw the original error:
 *   throw error
 *
 * Components should catch and handle with useGlobalSnackbar:
 *
 * Example usage in components:
 * ```tsx
 * import { useGlobalSnackbar } from '~/src/context/GlobalSnackbarContext'
 *
 * const { showSuccess, showError } = useGlobalSnackbar()
 *
 * try {
 *   await companyApi.createCompany(data)
 *   showSuccess('Company created successfully!')
 * } catch (error) {
 *   showError(error)
 * }
 * ```
 */
